import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Holds the "account suspended" state of the shell (ZEY-3046).
 *
 * Set by BillingLockInterceptor when any API answers 402 with `{ code: 'billing_locked' }`.
 * Once set it stays set until the page reloads: the suspended screen has no way out other than
 * paying or signing out.
 */
@Injectable({ providedIn: 'root' })
export class BillingLockService {
    private readonly subject = new BehaviorSubject<boolean>(false);
    readonly locked$: Observable<boolean> = this.subject.asObservable();

    get locked(): boolean {
        return this.subject.value;
    }

    lock(): void {
        if (!this.subject.value) this.subject.next(true);
    }
}

export const BILLING_LOCKED_CODE = 'billing_locked';

function hasLockedCode(body: unknown): boolean {
    if (!body || typeof body !== 'object') return false;
    const b = body as { code?: unknown; extensions?: { code?: unknown } };
    return b.code === BILLING_LOCKED_CODE || b.extensions?.code === BILLING_LOCKED_CODE;
}

/**
 * Reads the code out of an already-parsed 402 body. Strings are parsed as JSON when they look
 * like it; the plain-text "Tenant is inactive." body (TenantResolutionMiddleware) is not a
 * billing lock and keeps its current handling (none — the error is just rethrown).
 */
export function isBillingLockedBody(body: unknown): boolean {
    if (typeof body === 'string') {
        const t = body.trim();
        if (!t.startsWith('{')) return false;
        try {
            return hasLockedCode(JSON.parse(t));
        } catch {
            return false;
        }
    }
    return hasLockedCode(body);
}
